/**
 * Utility functions for handling mixed ID formats (UUID and PST format)
 * This provides backward compatibility while transitioning to PST format
 */

/**
 * Check if an ID is in UUID format
 */
export function isUUID(id: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
}

/**
 * Check if an ID is in PST format
 */
export function isPSTFormat(id: string): boolean {
  return id.startsWith('PST') && id.length > 3;
}

/**
 * Format a post ID for display
 * - PST format IDs are shown as-is
 * - UUID format IDs are shown with a shortened version
 */
export function formatPostIdForDisplay(id: string): string {
  if (!id) return 'Unknown';
  
  if (isPSTFormat(id)) {
    return id;
  }
  
  if (isUUID(id)) {
    // For UUIDs, show first 8 characters for brevity
    return `${id.substring(0, 8)}...`;
  }
  
  // Unknown format, return as-is
  return id;
}

/**
 * Get a consistent sort key for mixed ID formats
 * This ensures proper sorting regardless of ID format
 */
export function getIdSortKey(id: string): string {
  if (isPSTFormat(id)) {
    // Extract timestamp portion from PST format
    // PST format: PST + timestamp(base36) + random
    // The timestamp portion starts after 'PST'
    return id.substring(3);
  }
  
  // For UUIDs and other formats, use as-is
  return id;
}

/**
 * Validate if a post ID is in a valid format
 */
export function isValidPostId(id: string): boolean {
  return isPSTFormat(id) || isUUID(id);
}